import React from 'react';
import { useTranslation } from 'react-i18next';
import { WeezHeader, ToastContext, Button, Icon } from '@weezevent/nacre';
import { useHistory } from 'react-router-dom';
import { default as PartnerModel } from '../../models/partner';
import ProgressGuestsModel from '../../models/progressGuestsModel';
import Loading from '../../components/loading';
import PropTypes from 'prop-types';

import { AttendeesList, StatsCardFetch } from '@weezevent/socium';
import Attendee from '../../models/attendeeModel';
import Inventory from '../../models/inventory';
import Rate from '../../models/rateModel';
import PartnerForm from '../../models/partnerForm';
import { Booking } from '../../models/bookingModel';
import { BaseAPIModel, ArgumentError } from '@weezevent/weezjs-core';

function StatsContainer({ partnerId, attendeeInProgress, BatchStats }) {
    return <StatsCardFetch partnerId={partnerId} attendeeInProgress={attendeeInProgress} batchStatsModel={BatchStats} googleAnalytics={{ event: () => {} }} />;
}

StatsContainer.propTypes = {
    partnerId: PropTypes.string,
    attendeeInProgress: PropTypes.number,
    BatchStats: PropTypes.object,
    onClick: PropTypes.func
};

export default function PartnerDetails({ organization, partnerId }) {
    const { t } = useTranslation();
    let history = useHistory();

    const toast = React.useContext(ToastContext);

    const [openSideBar, setOpenSideBar] = React.useState(false);
    const [attendeeInProgress, setAttendeeInProgress] = React.useState(0);

    const [partner, loading] = PartnerModel.useApiModel(
        {
            id: partnerId,
            organizationId: organization.id,
            onError: e => {
                console.error(e);
                toast.error(t('ticket.partners.retrieve.error'));
            }
        },
        [partnerId, organization]
    );

    class InventoryModel extends Inventory {
        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/inventories`;
        }
    }

    class AttendeeModel extends Attendee {
        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/guests`;
        }

        static bulk_delete({ organizationId, partnerId, data } = {}) {
            if (!partnerId) {
                throw new ArgumentError('[Model][Attendee] partnerId is mandatory');
            }

            if (!organizationId) {
                throw new ArgumentError('[Model][Attendee] organizationId is mandatory');
            }
            let url = `/ticket/organizations/${organizationId}/partners/${partner.pk}/guests`;
            return BaseAPIModel.fetcher.delete(url, { data });
        }
    }
    class RateModel extends Rate {
        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/rates`;
        }
    }

    class AttendeeCreationRequestModel extends BaseAPIModel {
        fields() {
            return {
                uuid: { readOnly: true, primaryKey: true }
            };
        }
        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/attendees`;
        }
    }

    class BookingModel extends Booking {
        fields() {
            return {
                booking_uuid: { readOnly: true, primaryKey: true }
            };
        }

        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/bookings`;
        }
    }

    class MailStatusModel extends BaseAPIModel {
        fields() {
            return {
                id: { readOnly: true, primaryKey: true }
            };
        }
        static listUrl({ partnerId, attendeeId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/guests/${attendeeId}/mails`;
        }
    }

    class BatchStats extends BaseAPIModel {
        static getItemUrl(...args) {
            return this.listUrl(...args);
        }

        static listUrl({ partnerId }) {
            return `/ticket/organizations/${organization.pk}/partners/${partnerId}/stats-partner`;
        }
    }

    const headerProps = {
        rightComponent: [
            <Button
                key={'add_attendee'}
                primary
                disabled={partner?.isDeleted()}
                icon={<Icon name="plus" />}
                label={t('partners.invitation.add_guests')}
                onClick={() => {
                    setOpenSideBar(true);
                }}
            />
        ]
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <WeezHeader title={partner.name} backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners`) }} {...headerProps} />
            <div>
                <StatsContainer BatchStats={BatchStats} attendeeInProgress={attendeeInProgress} partnerId={partnerId} />
                <AttendeesList
                    partner={partner}
                    addAttendee={openSideBar}
                    setAddAttendee={setOpenSideBar}
                    onAttendeeInProgress={setAttendeeInProgress}
                    inventoryModel={InventoryModel}
                    attendeeModel={AttendeeModel}
                    rateModel={RateModel}
                    formModel={PartnerForm}
                    batchStatsModel={BatchStats}
                    attendeeCreationRequestModel={AttendeeCreationRequestModel}
                    bookingModel={BookingModel}
                    mailStatusModel={MailStatusModel}
                    googleAnalytics={{ event: () => {} }}
                    progressGuestsModel={ProgressGuestsModel}
                    organizationId={organization.pk}
                />
            </div>
        </>
    );
}

PartnerDetails.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string
};
